import React, { useState } from 'react';

import request from '@nineeng/request';

import SectionContainer from '../layout/SectionContainer.jsx';
import SectionNav from '../layout/SectionNav.jsx';
import WelcomeScreen from '../layout/WelcomeScreen.jsx';
import VATField from '../components/VATField.jsx';
import TextField from '../components/BasicMaterial.jsx';
import DatePicker from '../components/DatePicker.jsx';

import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';

import ContentContainer from '../layout/ContentContainer.jsx';
import DocumentsPage from './components/DocumentsPage.jsx';

import { getTextDefaults } from './translations/vcaTranslations';
import { Fragment } from 'react';

import { format, parseISO } from 'date-fns';
import { normalizeText } from 'normalize-text';
import levenshtein from 'js-levenshtein';
import { Link } from '@mui/material';

const WelcomePage = ({ input, text, onInput, vcaType, disabled }) => {
	return (
		<ContentContainer>
			<Typography variant='body1' sx={{ marginBottom: 4 }}>{text.intro}</Typography>

			{vcaType === 'company' && <>
				<VATField
					label={text.lblVAT}
					onChange={onInput}
					name='input.vatNumber'
					value={input.vatNumber || ''}
					disabled={disabled}
				/>
			</>}

			{vcaType === 'user' && <>
				<TextField
					label={text.lblFirstName}
					onChange={onInput}
					name='input.firstName'
					value={input.firstName || ''}
					disabled={disabled}
				/>
				<TextField
					label={text.lblLastName}
					onChange={onInput}
					name='input.lastName'
					value={input.lastName || ''}
					disabled={disabled}
				/>
				<DatePicker
					label={text.lblDOB}
					onChange={onInput}
					name='input.dateOfBirth'
					value={input.dateOfBirth || ''}
					disabled={disabled}
				/>
			</>}
		</ContentContainer>
	);
};

const ProcessingPage = () => {
	return (
		<WelcomeScreen>
			<Typography sx={{ textAlign: 'center' }}><CircularProgress /></Typography>
			<Typography sx={{ textAlign: 'center' }}>Processing...</Typography>
		</WelcomeScreen>
	);
};

const UploadPage = ({ values, isCompanyVCA, onInput, text, uploadUrl, disabled, useCamera, cameraIncludes, cameraRotationAngle, questionnaireUrl }) => {
	const key = isCompanyVCA ? 'groupDocuments' : 'userDocuments';
	const type = isCompanyVCA ? 'vca-company' : 'vca-user';

	const label = isCompanyVCA ? 
		(questionnaireUrl ? text.lblDocUploadLineQuestionnaireCompany : text.lblDocUploadLineCompany) :
		(questionnaireUrl ? text.lblDocUploadLineQuestionnaireUser : text.lblDocUploadLineUser);

	const explainer = questionnaireUrl ?
		<Typography variant='body1'>
			{`${isCompanyVCA ? text.lblManualUploadCompany : text.lblManualUploadUser}. `} 
			<Link underline='always' href={questionnaireUrl} target='_blank'>
				{`${isCompanyVCA ? text.lblQuestionnaireCompany : text.lblQuestionnaireUser}.`}
			</Link>
		</Typography>
		:
		<Typography variant='body1'>{`${isCompanyVCA ? text.lblManualUploadCompany : text.lblManualUploadUser}:`}</Typography>;


	return (
		<DocumentsPage
			requiredDocuments={[type]}
			labels={{ [type]: label }}
			onChange={onInput}
			uploadUrl={uploadUrl}
			value={values[key] || []}
			text={text}
			name={key}
			explainer={explainer}
			disabled={disabled}
			useCamera={useCamera}
			cameraIncludes={cameraIncludes}
			cameraRotationAngle={cameraRotationAngle}
		/>
	);
};

const DetailsPage = ({ text, certificates, disabled }) => {
	return (
		<WelcomeScreen>
			<Typography variant='body1' sx={{ marginBottom: 4 }}>{text.lblCertificates}</Typography>
			{certificates?.map(c => {
				const { id, validUntil, certificateIssuer, certificateType, certificateNumber } = c;
				return (
					<Card key={id} variant="outlined" sx={{ '& p': { margin: 0 }, marginBottom: 2 }}>
						<ListItem
							sx={{ height: 100 }}
							disabled={disabled}
						>
							<ListItemIcon sx={{ minWidth: 80 }}>
								<CheckIcon />
							</ListItemIcon>
							<ListItemText
								primary={certificateType}
								primaryTypographyProps={{ color: 'green', noWrap: true }}
								secondary={<Fragment>
									<Typography>{certificateNumber}</Typography>
									<Typography variant='caption'>{certificateIssuer} - Expires on {format(new Date(validUntil), 'dd/MM/yyyy')}</Typography>
								</Fragment>}
							/>
						</ListItem>
					</Card>
				);
			})}
		</WelcomeScreen>
	);
};

const FinishedPage = ({ text }) => {
	return (
		<WelcomeScreen>
			<Typography variant='h6' sx={{ marginBottom: 2 }}>{text.finishedTitle}</Typography>
			<Typography>{text.finishedReason}</Typography>
		</WelcomeScreen>
	);
};

const VCASection = ({
	onChange = () => { },
	values = {},
	textOverride = {},
	background = 'light',
	navigationProps,
	selectedLanguage = 'EN',
	apiUrl = '/api/validate/vca',
	uploadUrl = '/api/files',
	vcaType = 'company',
	disabled = false,
	acceptedTypes = [],
	useCamera = true,
	cameraIncludes = '',
	cameraRotationAngle = 0,
	questionnaireUrl,
	...rest
}) => {
	const textDefaults = getTextDefaults(selectedLanguage);
	const text = Object.assign({}, textDefaults, textOverride);
	const defaultPage = 'welcome';
	const isCompanyVCA = (vcaType === 'company');
	const name = isCompanyVCA ? 'groupCertificates' : 'userCertificates';

	const certificates = values[name] || [];
	const input = values.input || {};

	const screens = {
		welcome: {
			next: () => handleCheck(input),
			component: WelcomePage,
			canContinue: isCompanyVCA ? !!input.vatNumber : !!(input.lastName && input.dateOfBirth),
		},
		processing: {
			component: ProcessingPage,
		},
		upload: {
			previous: () => setCurrentScreen('welcome'),
			next: () => setCurrentScreen('finished'),
			component: UploadPage,
			canContinue: values.groupDocuments?.length || values.userDocuments?.length,
		},
		details: {
			previous: () => setCurrentScreen('welcome'),
			next: () => setCurrentScreen('finished'),
			component: DetailsPage,
			canContinue: true,
		},
		finished: {
			// previous: () => setCurrentScreen('details'),
			next: (event) => navigationProps.next(event),
			component: FinishedPage,
			canContinue: true,
		},
	};

	const handleCheck = async input => {
		try {
			setCurrentScreen('processing');

			let certs;
			if (vcaType === 'user') {
				const url = `${apiUrl}/user/${input.lastName}/${format(parseISO(input.dateOfBirth), 'yyyy-MM-dd')}`;
				const { body } = await request.get(url);
				if (!body || !body.length) throw new Error('No certs');
				certs = body.filter(c => {
					const distanceFirstName = levenshtein(normalizeText(c.firstName), normalizeText(input.firstName));
					if (distanceFirstName < 2) return true;
				});
			}

			if (vcaType === 'company') {
				const url = `${apiUrl}/company/${input.vatNumber}`;
				const { body } = await request.get(url);
				if (!body || !body.length) throw new Error('No certs');
				certs = body;
			}

			// Filter for acceptedTypes
			const value = acceptedTypes?.length ? certs.filter(c => acceptedTypes.includes(c.type)) : certs;
			if (!value.length) throw new Error('No certs');
			onChange({ target: { name, value } });

			setCurrentScreen('details');
		} catch (error) {
			setCurrentScreen('upload');
			console.error(error);
		}
	};

	const handleChange = ({ target }) => onChange({ target: { ...target, name } });
	const handleInput = ({ target }) => onChange({ target: { ...target, name: target.name } });

	const [currentScreen, setCurrentScreen] = useState(defaultPage);
	const screen = screens[currentScreen] || screens.welcome;
	const screenProps = {
		input,
		isCompanyVCA,
		values,
		certificates,
		onChange: handleChange,
		onInput: handleInput,
		text,
		vcaType,
		setCurrentScreen,
		uploadUrl,
		disabled,
		useCamera,
		cameraIncludes,
		cameraRotationAngle,
		questionnaireUrl,
	};

	return (
		<SectionContainer
			background={background}
			Navigation={
				<SectionNav
					{...navigationProps}
					hasPrevious={!!screen.previous}
					hasNext={currentScreen !== 'finished' || navigationProps.hasNext}
					canContinue={!!screen.canContinue}
					next={screen.next}
					previous={screen.previous}
					selectedLanguage={selectedLanguage}
				/>
			}
		>
			{screen && <screen.component {...screenProps} />}
		</SectionContainer>
	);
};

VCASection.type = 'VCASection';
VCASection.hasNavigation = true;
VCASection.spec = [{
	name: 'VCASection',
	key: 'VCASection',
	allowedFields: [],
	translatable: ['navigation.previousLabel', 'navigation.nextLabel', 'navigation.submitLabel'],
	fields: [
		{
			type: 'AutoCompleteByID',
			name: 'vcaType',
			label: 'VCA Type',
			options: [
				{ id: 'user', label: 'User' },
				{ id: 'company', label: 'Company' },
			],
			required: true,
		},
		{
			type: 'Checkbox',
			name: 'useCamera',
			label: 'Force camera only (kiosk environments only — requires device to be registered)',
		},
	],
	defaultProperties: {
		refDataMap: {
			'input.firstName': 'user.firstName',
			'input.lastName': 'user.lastName',
			'input.dateOfBirth': 'user.userData.dateOfBirth',
			'input.vatNumber': 'group.uniqueId',
		},
		title: 'VCA section',
	},
}];


export default VCASection;
